"use client";

import { useTranslate } from "@tolgee/react";
import { useState } from "react";
import { Button } from "#/components/ui/button.tsx";
import { cn } from "#/lib/utils";
import { useTradeRecentTrades } from "../hooks/use-trade-data.ts";
import { useTradeSymbol } from "../hooks/use-trade-symbol.ts";
import { MARKET_TRADES_ROW_LIMIT } from "./market-trades/constants/market-trades.constants.ts";
import { MarketTradesTable } from "./market-trades/market-trades-table.tsx";
import {
	type ChartSubTab,
	MobileChartSubTabs,
} from "./mobile/mobile-chart-sub-tabs.tsx";
import { MobileMarketHeader } from "./mobile/mobile-market-header.tsx";
import { MobileOrderForm } from "./mobile/mobile-order-form.tsx";
import { Orderbook } from "./orderbook/orderbook.tsx";
import { Positions } from "./positions/positions.tsx";
import { Chart } from "./trade-chart/chart.tsx";

function MobileMarketTrades() {
	const { trades } = useTradeRecentTrades();
	const { coin } = useTradeSymbol();
	return (
		<div className="flex h-full flex-col bg-trade-surface">
			<MarketTradesTable
				trades={trades.slice(0, MARKET_TRADES_ROW_LIMIT)}
				coin={coin}
			/>
		</div>
	);
}

export function MobileTradeLayout() {
	const { t } = useTranslate();
	const [chartSubTab, setChartSubTab] = useState<ChartSubTab>("chart");
	return (
		<div className="relative flex h-[calc(100dvh-48px)] flex-col">
			<MobileMarketHeader />

			<div
				className={cn(
					"min-h-0 flex-1 overflow-y-auto",
					chartSubTab === "chart" && "pb-20",
				)}
			>
				<div>
					<MobileChartSubTabs active={chartSubTab} onChange={setChartSubTab} />

					<div className={cn("h-[56vh]", chartSubTab !== "chart" && "!hidden")}>
						<Chart />
					</div>

					<div
						className={cn("h-[60vh]", chartSubTab !== "orderbook" && "!hidden")}
					>
						<div className="grid h-full grid-cols-[minmax(0,1fr)_minmax(116px,38vw)] border-b border-trade-border-subtle">
							<div className="min-w-0 overflow-hidden border-r border-trade-border-subtle">
								<MobileOrderForm />
							</div>
							<div className="min-w-0 overflow-hidden">
								<Orderbook />
							</div>
						</div>
					</div>

					{chartSubTab === "trades" && (
						<div className="h-dvh">
							<MobileMarketTrades />
						</div>
					)}

					{chartSubTab !== "trades" && (
						<div className="h-[40vh]">
							<Positions />
						</div>
					)}
				</div>
			</div>

			{chartSubTab !== "orderbook" && (
				<div className="absolute inset-x-0 bottom-0 z-20 border-t border-trade-border-subtle bg-trade-surface/95 px-3 py-3 backdrop-blur supports-[backdrop-filter]:bg-trade-surface/90">
					<div className="grid grid-cols-2 gap-2">
						<Button
							type="button"
							onClick={() => setChartSubTab("orderbook")}
							className="h-9 rounded-[8px] bg-trade-green text-[var(--color-theme-static-white)] hover:bg-trade-green"
						>
							{t("order.side.buy")}
						</Button>
						<Button
							type="button"
							onClick={() => setChartSubTab("orderbook")}
							className="h-9 rounded-[8px] bg-trade-red text-[var(--color-theme-static-white)] hover:bg-trade-red"
						>
							{t("order.side.sell")}
						</Button>
					</div>
				</div>
			)}
		</div>
	);
}
